import { TranslocoPipe } from '@jsverse/transloco';
import { Component, computed, inject } from '@angular/core';
import { EntitlementService } from '../../../core/entitlements/entitlement.service';
import { Feature, minPlanFor } from '../../../core/entitlements/entitlements.models';
import { UpgradeSheetService } from '../../../core/entitlements/upgrade-sheet.service';
import { AppSheetComponent } from '../app-sheet/app-sheet.component';
import { PlanBadgeComponent } from '../plan-badge/plan-badge.component';

interface PlanSummary {
  plan: 'free' | 'pro' | 'community';
  name: string;
  price: string;
  features: string[];
}

const PLAN_SUMMARIES: PlanSummary[] = [
  {
    plan: 'free',
    name: 'Free',
    price: 'Free forever',
    features: ['Match logging & live scoring', 'Full single-match review'],
  },
  {
    plan: 'pro',
    name: 'Pro',
    price: '4,99 € / month',
    features: ['Cross-match stats dashboard', 'Filter, compare & track progress'],
  },
  {
    plan: 'community',
    name: 'Community',
    price: '24,99 € / month',
    features: ['Everything in Pro', '3 Pro accounts included for your trainers', 'Up to 3 communities, 200 members each', 'Custom groups', 'Member invites'],
  },
];

const FEATURE_NAMES: Partial<Record<Feature, string>> = {
  statsDashboardCharts: 'the full chart dashboard',
  statsPressureTab: 'pressure analytics',
  statsTrendsTab: 'trend analytics',
  statsMatchupsTab: 'matchup records',
  statsTeammatesTab: 'teammate chemistry',
  comparePlayers: 'player comparison',
  multipleCommunities: 'multiple communities',
  largeCommunity: 'larger communities',
};

/**
 * The single app-wide upgrade sheet, rendered once in the AppShell and opened through
 * UpgradeSheetService. Purely informational until payments exist: it explains the plan ladder and
 * how a plan is unlocked today (redeem code).
 */
@Component({
  selector: 'app-upgrade-sheet',
  standalone: true,
  imports: [TranslocoPipe, AppSheetComponent, PlanBadgeComponent],
  template: `
    <app-sheet [open]="open()" [sheetTitle]="'core.plans' | transloco" width="lg" (dismissed)="close()">
      @if (featureName()) {
        <p class="lead">
          Unlock {{ featureName() }} with the
          <strong>{{ requiredPlanName() }}</strong>  {{ 'core.plan' | transloco }}
        </p>
      }
      <div class="plans">
        @for (summary of planSummaries; track summary.plan) {
          <div class="plan" [class.plan--current]="summary.plan === currentPlan()">
            <div class="plan-head">
              <app-plan-badge [plan]="summary.plan" [label]="''" />
              <span class="price">{{ summary.price }}</span>
              @if (summary.plan === currentPlan()) {
                <span class="current-tag">{{ 'core.yourPlan' | transloco }}</span>
              }
            </div>
            <ul>
              @for (feature of summary.features; track feature) {
                <li>{{ feature }}</li>
              }
            </ul>
          </div>
        }
      </div>
      <p class="footnote">{{ 'core.upgradeViaRedeemCode' | transloco }}</p>
      <button sheet-actions type="button" class="btn btn-primary" (click)="close()">{{ 'core.gotIt' | transloco }}</button>
    </app-sheet>
  `,
  styles: [`
    .lead {
      font-size: var(--font-size-body);
      margin: 0;
    }

    .plans {
      display: grid;
      gap: var(--space-3);
    }

    .plan {
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-lg);
      display: grid;
      gap: var(--space-2);
      padding: var(--space-3) var(--space-4);
    }

    .plan--current {
      border-color: var(--color-action);
    }

    .plan-head {
      align-items: center;
      display: flex;
      gap: var(--space-3);
    }

    .price {
      color: var(--color-text-secondary);
      font-size: var(--font-size-caption);
      font-weight: 700;
    }

    .current-tag {
      color: var(--color-action);
      font-size: var(--font-size-micro);
      font-weight: 900;
      margin-left: auto;
      text-transform: uppercase;
    }

    ul {
      color: var(--color-text-secondary);
      font-size: var(--font-size-caption);
      margin: 0;
      padding-left: 1.1rem;
    }

    .footnote {
      color: var(--color-text-secondary);
      font-size: var(--font-size-micro);
      margin: 0;
    }
  `],
})
export class UpgradeSheetComponent {
  private readonly sheet = inject(UpgradeSheetService);
  private readonly entitlements = inject(EntitlementService);

  readonly planSummaries = PLAN_SUMMARIES;
  readonly open = computed(() => this.sheet.state() !== null);
  readonly currentPlan = this.entitlements.plan;
  readonly featureName = computed(() => {
    const feature = this.sheet.state()?.feature ?? null;
    return feature ? (FEATURE_NAMES[feature] ?? null) : null;
  });
  readonly requiredPlanName = computed(() => {
    const feature = this.sheet.state()?.feature ?? null;
    if (!feature) {
      return 'Pro';
    }
    return minPlanFor(feature) === 'community' ? 'Community' : 'Pro';
  });

  close(): void {
    this.sheet.close();
  }
}
